import './sprites.scss'
import { useEffect, useRef, useState } from 'react'
import { concurrentQueue, delay as delayFun } from '../../utils'
import { requestAnimationFrame } from '@base/bom'

type SrcList = Array<{
  url: string
  frameStart: number
  frameEnd: number
}>

type ImgList<T> = Array<{
  item: T
  frameStart: number
  frameEnd: number
} | null>

interface SpritesProps {
  src: string | SrcList
  width: number
  height: number
  frames: number
  poster?: string
  className?: string
  corssOrigin?: boolean
  loop?: boolean
  autoplay?: boolean
  play?: boolean
  pause?: boolean
  interval?: number
  delay?: number
  fps?: number
  style?: React.CSSProperties
  fillMode?: 'forwards' | 'backwards'
  onReady?: () => void
  onFirstFrame?: () => void
  onFrame?: (frame: number) => void
  onEnd?: () => void
}

export default function Sprites(props: SpritesProps) {
  const {
    src,
    width,
    height,
    frames,
    poster,
    loop,
    autoplay,
    play,
    pause,
    interval,
    delay,
    fps,
    className,
    corssOrigin,
    style,
    fillMode,
    onReady,
    onFirstFrame,
    onFrame,
    onEnd,
  } = Object.assign<Partial<SpritesProps>, SpritesProps>(
    {
      corssOrigin: false,
      fps: 60,
      loop: false,
      autoplay: true,
      play: false,
      pause: false,
      interval: 0,
      delay: 0,
      fillMode: 'backwards',
    },
    props
  )

  const supportOffscreenCanvas = 'OffscreenCanvas' in window
  // android5使用ImageBitmap页面会崩溃
  // const supportImageBitmap = 'createImageBitmap' in window
  // const supportImageBitmap = false

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const offscreenCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const ctx = useRef<CanvasRenderingContext2D | null>(null)
  const currentFrame = useRef(-1)
  const loaded = useRef(false)
  const privatePause = useRef<boolean | undefined>(false)
  const privatePlay = useRef<boolean | undefined>(false)
  const playing = useRef(false)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    privatePause.current = pause
    privatePlay.current = play
    if (playing.current) return

    playing.current = true

    if (!(singleSrc(src) || multipleSrc(src))) {
      throw new TypeError('Invalid src')
    }

    let offscreenCanvas: OffscreenCanvas | HTMLCanvasElement | null
    let offscreenCtx:
      | OffscreenCanvasRenderingContext2D
      | CanvasRenderingContext2D
      | null
    let framesImageData: ImageData[] = []

    // 离屏渲染
    if (supportOffscreenCanvas) {
      offscreenCanvas = new OffscreenCanvas(width, height)
      offscreenCtx = offscreenCanvas.getContext('2d', {
        willReadFrequently: true,
      })
    } else {
      offscreenCanvas = offscreenCanvasRef.current
      offscreenCtx = offscreenCanvasRef.current
        ? offscreenCanvasRef.current.getContext('2d')
        : null
    }

    let loadImg$: Promise<ImgList<HTMLImageElement | ImageBitmap>> =
      Promise.resolve([])
    if (singleSrc(src)) {
      loadImg$ = preloadImg({
        url: src,
        frameStart: 0,
        frameEnd: frames - 1,
      })
        .then((img) => [img])
        .catch(() => [null])
    } else if (multipleSrc(src)) {
      const getImgList = () => {
        let taskList: Array<() => Promise<ImgList<HTMLImageElement>[number]>> =
          []
        for (const _src of src) {
          taskList.push(() => preloadImg(_src))
        }
        return concurrentQueue(taskList, 2)
      }
      loadImg$ = getImgList()
    }
    function preloadImg(_src: SrcList[number]) {
      return new Promise<ImgList<HTMLImageElement>[number]>(
        (resolve, reject) => {
          const img = new Image()
          img.onload = () => {
            let i = _src.frameStart
            let lastTime = 0
            function render(timestamp: number) {
              const deltaTime = timestamp - lastTime
              const frameInterval = 1000 / 30
              if (deltaTime >= frameInterval) {
                lastTime = timestamp - (deltaTime % frameInterval)
                if (i <= _src.frameEnd) {
                  const n = i - _src.frameStart
                  if (offscreenCtx) {
                    offscreenCtx.clearRect(0, 0, width, height)
                    offscreenCtx.drawImage(img, -(width * n), 0)
                    framesImageData[i] = offscreenCtx.getImageData(
                      0,
                      0,
                      width,
                      height
                    )

                    i++
                    requestAnimationFrame(render)
                  }
                } else {
                  resolve({
                    item: img,
                    frameStart: _src.frameStart,
                    frameEnd: _src.frameEnd,
                  })
                }
              } else {
                requestAnimationFrame(render)
              }
            }
            requestAnimationFrame(render)
          }
          img.onerror = (e) => reject(e)
          if (corssOrigin) {
            img.crossOrigin = 'Anonymous'
          }
          img.src = _src.url
        }
      )
    }

    loadImg$.then((imgList) => {
      if (
        !Array.isArray(imgList) ||
        !imgList.length ||
        imgList.some((v) => v == null)
      )
        return

      onReady instanceof Function && onReady()

      delayFun(delay).then(() => {
        ctx.current = canvasRef.current
          ? canvasRef.current.getContext('2d')
          : null
        currentFrame.current = -1
        requestAnimationFrame(heartbeat)
      })

      const onFrameIsFunc = onFrame instanceof Function
      const onEndIsFunc = onEnd instanceof Function
      function renderFirstFrame() {
        ctx.current && ctx.current.putImageData(framesImageData[0], 0, 0)
      }
      function draw() {
        if ((!autoplay && !privatePlay.current) || privatePause.current) return

        if (currentFrame.current >= frames - 1) {
          if (!loop) {
            if (!autoplay) {
              privatePlay.current = false
              currentFrame.current = -1
            }
            return onEndIsFunc && onEnd()
          } else {
            currentFrame.current = -1
          }
        }

        ++currentFrame.current
        onFrameIsFunc && onFrame(currentFrame.current)

        const i = currentFrame.current
        if (i === 0) {
          if (!autoplay || fillMode === 'forwards') {
            renderFirstFrame()
          }
        }
        if (i === 1) {
          got()
        }
        ctx.current && ctx.current.putImageData(framesImageData[i], 0, 0)
      }

      function got() {
        if (!loaded.current) {
          loaded.current = true
          setReady(true)
          onFirstFrame instanceof Function && onFirstFrame()
        }
      }

      let lastTime = 0
      function heartbeat(timestamp: number) {
        // 计算当前时间与上次更新时间的差值
        const deltaTime = timestamp - lastTime
        const frameInterval = 1000 / (fps as number)

        // 如果差值大于等于每帧间隔，则更新和绘制
        if (deltaTime >= frameInterval) {
          lastTime = timestamp - (deltaTime % frameInterval) // 更新 lastTime
          // 在这里添加绘制逻辑
          draw()
        }

        requestAnimationFrame(heartbeat)
      }
    })

    function singleSrc(_src: unknown): _src is string {
      return typeof _src === 'string'
    }

    function multipleSrc(_src: unknown): _src is SrcList {
      return Array.isArray(_src)
    }
  }, [src, play, pause])

  return (
    <div className={`sprites${className ? ` ${className}` : ''}`} style={style}>
      {poster ? (
        <img src={poster} alt="" className={ready ? 'sprites-img-hide' : ''} />
      ) : null}
      {!supportOffscreenCanvas ? (
        <canvas
          ref={offscreenCanvasRef}
          className="offscreen-canvas"
          width={width}
          height={height}
        ></canvas>
      ) : null}
      <canvas
        ref={canvasRef}
        className="main-canvas"
        width={width}
        height={height}
      ></canvas>
    </div>
  )
}
